
import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Check, User, Bell, Star, Users, LogOut } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ReferralModal from '@/components/referral/ReferralModal';
import ReferralHistorySection from '@/components/profile/ReferralHistorySection';

const Profile = () => {
  const { user, isAuthenticated, isLoading, signOut } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('account');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

  const handleSave = () => {
    setIsUpdating(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsUpdating(false);
      toast({
        title: "Profil aktualisiert",
        description: "Deine Änderungen wurden gespeichert.",
      });
    }, 1000);
  };
  
  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Abgemeldet",
        description: "Du wurdest erfolgreich abgemeldet.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: "Bei der Abmeldung ist ein Fehler aufgetreten.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-chrona-accent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container py-8">
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle>Nicht angemeldet</CardTitle>
            <CardDescription>
              Bitte melde dich an, um dein Profil zu sehen.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button asChild>
              <a href="/auth">Zum Login</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const userInitials = user?.email ? user.email.substring(0, 2).toUpperCase() : 'U';

  return (
    <div className="container py-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-chrona-text">Mein Profil</h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left sidebar with user info */}
          <div className="lg:col-span-4">
            <Card className="border border-chrona-gray-700 bg-chrona-gray-900 shadow-lg mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center">
                  <Avatar className="w-24 h-24 mb-4">
                    <AvatarImage src="" />
                    <AvatarFallback className="bg-chrona-accent text-white text-2xl">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <h2 className="text-xl font-semibold text-chrona-text">{user?.email}</h2>
                  <p className="text-chrona-gray-400 mb-4">
                    Kunde seit {new Date(user?.created_at || Date.now()).toLocaleDateString('de-DE')}
                  </p>
                  <div className="w-full space-y-2 mt-4">
                    <Button variant="outline" className="w-full justify-start" onClick={() => setActiveTab('account')}>
                      <User className="mr-2 h-4 w-4" />
                      Persönliche Daten
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => setActiveTab('notifications')}>
                      <Bell className="mr-2 h-4 w-4" />
                      Benachrichtigungen
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => setActiveTab('favorites')}>
                      <Star className="mr-2 h-4 w-4" />
                      Favoriten
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => setActiveTab('referrals')}>
                      <Users className="mr-2 h-4 w-4" />
                      Einladungen
                    </Button>
                    <Button variant="destructive" className="w-full justify-start mt-8" onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Abmelden
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main content area */}
          <div className="lg:col-span-8">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 mb-6">
                <TabsTrigger value="account">Account</TabsTrigger>
                <TabsTrigger value="notifications">Benachrichtigungen</TabsTrigger>
                <TabsTrigger value="favorites">Favoriten</TabsTrigger>
                <TabsTrigger value="referrals">Einladungen</TabsTrigger>
              </TabsList>
              
              <TabsContent value="account">
                <Card className="border border-chrona-gray-700 bg-chrona-gray-900 shadow-lg">
                  <CardHeader>
                    <CardTitle>Persönliche Daten</CardTitle>
                    <CardDescription>
                      Bearbeite deine persönlichen Informationen.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstname">Vorname</Label>
                          <Input 
                            id="firstname" 
                            placeholder="Vorname" 
                            className="bg-chrona-gray-800 border-chrona-gray-700" 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastname">Nachname</Label>
                          <Input 
                            id="lastname" 
                            placeholder="Nachname" 
                            className="bg-chrona-gray-800 border-chrona-gray-700" 
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">E-Mail</Label>
                        <Input 
                          id="email" 
                          type="email" 
                          value={user?.email}
                          disabled 
                          className="bg-chrona-gray-800 border-chrona-gray-700" 
                        />
                        <p className="text-xs text-chrona-gray-400">
                          Die E-Mail-Adresse kann nicht geändert werden.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefon</Label>
                        <Input 
                          id="phone" 
                          type="tel" 
                          placeholder="+49 123 4567890" 
                          className="bg-chrona-gray-800 border-chrona-gray-700" 
                        />
                      </div>
                      <div className="pt-4">
                        <Button 
                          type="button" 
                          onClick={handleSave}
                          className="bg-chrona-accent hover:bg-chrona-accent-light"
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Wird gespeichert...
                            </>
                          ) : (
                            <>
                              <Check className="mr-2 h-4 w-4" />
                              Speichern
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="notifications">
                <Card className="border border-chrona-gray-700 bg-chrona-gray-900 shadow-lg">
                  <CardHeader>
                    <CardTitle>Benachrichtigungen</CardTitle>
                    <CardDescription>
                      Verwalte deine Benachrichtigungseinstellungen.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-chrona-gray-400">
                      Die Benachrichtigungseinstellungen werden demnächst verfügbar sein.
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="favorites">
                <Card className="border border-chrona-gray-700 bg-chrona-gray-900 shadow-lg">
                  <CardHeader>
                    <CardTitle>Meine Favoriten</CardTitle>
                    <CardDescription>
                      Hier findest du deine gespeicherten Lieblingsstudios.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-chrona-gray-400">
                      Du hast noch keine Favoriten gespeichert. Entdecke Studios und speichere sie als Favoriten.
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="referrals">
                <ReferralHistorySection onOpenReferralModal={() => setIsReferralModalOpen(true)} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Referral Modal */}
      <ReferralModal 
        isOpen={isReferralModalOpen} 
        onClose={() => setIsReferralModalOpen(false)}
      />
    </div>
  );
};

export default Profile;
